import React from 'react';
import PropTypes from 'prop-types';

import LoadTracker from '../../../../services/LoadTracker';
import GapChoice from './GapChoice';

import styles from './graphicGapMatchInteraction.scss';
import sessionReader from '../../../../store/readHelpers/SessionReader';

export const dimensionsAreDefined = dimensions => !!(dimensions && dimensions.width !== undefined && dimensions.height !== undefined);
export const highest = (int1, int2) => {
    return int2 > int1 ? int2 : int1;
};

class GapChoicesContainer extends React.Component {
    static propTypes = {
        adjustGapChoicesToBeTheSameSize: PropTypes.bool,
        eventTracker: PropTypes.object.isRequired,
        correctResponses: PropTypes.array,
        droppedGapChoices: PropTypes.array,
        gapChoiceModels: PropTypes.array,
        handleUserResponse: PropTypes.func.isRequired,
        handleGapChoiceDeleteKeyUp: PropTypes.func.isRequired,
        handleGapChoiceArrowKeyUp: PropTypes.func.isRequired,
        handleGapChoiceDragStart: PropTypes.func.isRequired,
        handleGapChoiceDragEnd: PropTypes.func.isRequired,
        isReadOnly: PropTypes.bool,
        reCalculatedGapChoiceDimensions: PropTypes.object,
        reportReCalculatedGapChoiceDimensions: PropTypes.func,
        reportReady: PropTypes.func.isRequired,
        reuseAlternatives: PropTypes.bool,
        services: PropTypes.object.isRequired,
        userResponse: PropTypes.array,
    };

    constructor(props) {
        super(props);
        this.state = { dragOver: false };
        this.gapChoiceDimensions = [];
        this.setTrackers();
    }

    componentWillUnmount() {
        this.readyTracker.kill();
        this.gapChoicesTracker.kill();
    }

    setTrackers = () => {
        const { gapChoiceModels, reportReady } = this.props;
        this.readyTracker = new LoadTracker(reportReady, 'GapChoicesContainer');
        this.gapChoicesTracker = new LoadTracker(this.handleGapChoicesReady, 'GapChoicesContainer > GapChoices tracker');
        this.gapChoicesReporter = this.readyTracker.addItemToTrack('GapChoicesContainer > GapChoices reporter');
        this.gapChoiceReporters = gapChoiceModels
            .reduce((trackers, gapChoiceModel) => {
                trackers[gapChoiceModel.identifier] = this.gapChoicesTracker.addItemToTrack(`GapChoice ${gapChoiceModel.identifier}`);
                return trackers;
            }, {});


        this.gapChoicesTracker.setAllItemsAdded();
        this.readyTracker.setAllItemsAdded();
    };

    handleGapChoicesReady = (success) => {
        if (this.props.adjustGapChoicesToBeTheSameSize) {
            this.setReCalculatedGapChoiceDimensions();
        }

        this.gapChoicesReporter(success);
    };

    setReCalculatedGapChoiceDimensions = () => {
        let newWidth = 0;
        let newHeight = 0;
        this.gapChoiceDimensions
            .filter(dimensionsAreDefined)
            .forEach(({ width, height }) => {
                newWidth = highest(width, newWidth);
                newHeight = highest(height, newHeight);
            });

        this.props.reportReCalculatedGapChoiceDimensions({ width: newWidth, height: newHeight });
    };

    getDraggedGapChoiceIdentifier = (e) => {
        try {
            const dragData = JSON.parse(e.dataTransfer.getData('text'));
            return dragData.gapChoiceIdentifier;
        } catch (error) {
            return undefined;
        }
    };

    handleDragEnter = (e) => {
        if (this.props.eventTracker.isValid(e)) {
            e.preventDefault();
            this.setState({ dragOver: true });
        }
    };

    handleDragOver = (e) => {
        if (this.props.eventTracker.isValid(e)) {
            e.preventDefault();
        }
    };

    handleDragLeave = (e) => {
        e.preventDefault();
        this.setState({ dragOver: false });
    };

    handleDrop = (e) => {
        const gapChoiceIdentifier = this.getDraggedGapChoiceIdentifier(e);
        if (gapChoiceIdentifier) {
            e.preventDefault();
            this.props.eventTracker.forget(e);
            this.props.handleUserResponse(gapChoiceIdentifier);
            this.setState({ dragOver: false });
        }
    };

    registerGapChoiceDimensions = dimensions => this.gapChoiceDimensions.push(dimensions);

    isDropped = gapChoiceIdentifier => this.props.droppedGapChoices.indexOf(gapChoiceIdentifier) !== -1;

    shouldHideGapChoice = (gapChoiceIdentifier) => {
        return !this.props.reuseAlternatives && this.isDropped(gapChoiceIdentifier);
    };

    renderGapChoiceElements() {
        const { gapChoiceModels } = this.props;
        return gapChoiceModels.map((gapChoiceModel, index) => {
            return (
                <GapChoice
                    correctResponses={this.props.correctResponses}
                    eventTracker={this.props.eventTracker}
                    gapChoiceModel={gapChoiceModel}
                    handleGapChoiceArrowKeyUp={this.props.handleGapChoiceArrowKeyUp}
                    handleGapChoiceDeleteKeyUp={this.props.handleGapChoiceDeleteKeyUp}
                    handleGapChoiceDragStart={this.props.handleGapChoiceDragStart}
                    handleGapChoiceDragEnd={this.props.handleGapChoiceDragEnd}
                    hidden={this.shouldHideGapChoice(gapChoiceModel.identifier)}
                    isReadOnly={this.props.isReadOnly}
                    isReusable={this.props.reuseAlternatives}
                    key={gapChoiceModel.identifier}
                    reCalculatedGapChoiceDimensions={this.props.reCalculatedGapChoiceDimensions}
                    reportDimensions={this.registerGapChoiceDimensions}
                    reportReady={this.gapChoiceReporters[gapChoiceModel.identifier]}
                    services={this.props.services}
                    userResponse={this.props.userResponse}
                    index={index}
                />
            );
        });
    }

    render() {
        const { reCalculatedGapChoiceDimensions } = this.state;
        const classNames = [styles.gapChoiceContainer, 'drop'];
        if (this.state.dragOver) {
            classNames.push('drag-over');
        }
        if (sessionReader.isPrintStylePaperBased()) {
            classNames.push('paper-based');
        }
        const style = { minHeight: reCalculatedGapChoiceDimensions ? `${reCalculatedGapChoiceDimensions.height + 12}px` : undefined };
        return (
            <div
                className={classNames.join(' ')}
                data-type="drop"
                onDragEnter={this.handleDragEnter}
                onDragOver={this.handleDragOver}
                onDragLeave={this.handleDragLeave}
                onDrop={this.handleDrop}
                ref={(element) => { this.rootElement = element; }}
                style={style}
            >
                {
                    this.renderGapChoiceElements()
                }
            </div>
        );
    }
}

export default GapChoicesContainer;
